import Project from '@theme/global-functions';

class SygnisoftPopup {
  constructor(module) {
    this.module = module;
    this.popupId = parseInt(this.module.dataset.popupId, 10);

    // Walidacja ID
    if (isNaN(this.popupId) || this.popupId <= 0) {
      console.error('Invalid popup ID:', this.popupId);
      return;
    }

    this.overlayElement = document.querySelector('.modals-block__overlay');
    this.closeButton = this.module.querySelector('[data-popup-close]');
    this.isOpen = false;
    this.settings = null;

    // Znajdź ustawienia dla tego popupa
    if (
      typeof sygnisoftPopupsSettings !== 'undefined' &&
      Array.isArray(sygnisoftPopupsSettings)
    ) {
      this.settings = sygnisoftPopupsSettings.find(
        (s) => s.id === this.popupId
      );
    }

    if (!this.settings) {
      console.warn(
        `Brak ustawień dla popup ${this.popupId}, używam domyślnych`
      );
      this.settings = {
        id: this.popupId,
        auto_open: false,
        delay: 0,
        show_once: false,
        overlay_close: true,
        animation: 'fade',
      };
    }
  }

  init() {
    // Sprawdź cookie jeśli popup ma się pokazać tylko raz
    if (
      this.settings.show_once &&
      this.getCookie(`popup_shown_${this.popupId}`)
    ) {
      return;
    }

    // Przycisk zamknięcia
    if (this.closeButton) {
      this.closeButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.close();
      });
    }

    // Zamykanie przez overlay
    const overlayClose = this.module.dataset.overlayClose === 'true';
    if (overlayClose && this.overlayElement) {
      this.overlayClickHandler = (e) => {
        if (e.target === this.overlayElement && this.isOpen) {
          this.close();
        }
      };
      this.overlayElement.addEventListener('click', this.overlayClickHandler);
    }

    // ESC key
    this.escHandler = (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    };
    document.addEventListener('keydown', this.escHandler);

    // Automatyczne otwieranie z walidacją delay
    if (this.settings.auto_open) {
      const delay = Math.max(0, Math.min(this.settings.delay, 60)); // Max 60 sekund
      setTimeout(() => {
        this.open();
      }, delay * 1000);
    }

    // Nasłuchuj na custom event do manualnego otwierania
    this.openHandler = () => this.open();
    document.addEventListener(`openPopup${this.popupId}`, this.openHandler);
  }

  open() {
    if (this.isOpen || !this.module) return;

    this.isOpen = true;

    // Pokaż overlay
    if (this.overlayElement) {
      this.overlayElement.classList.add('is-visible');
    }

    // Pokaż popup
    this.module.classList.add('is-visible');

    // Zapisz cookie jeśli potrzebne
    if (this.settings.show_once) {
      this.setCookie(`popup_shown_${this.popupId}`, 'true', 30);
    }

    // Zablokuj scroll z zachowaniem szerokości scrollbara
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = `${scrollbarWidth}px`;

    // Focus trap - ustaw focus na popup
    this.previousActiveElement = document.activeElement;

    // Focus na przycisk zamknięcia
    requestAnimationFrame(() => {
      if (this.closeButton) {
        this.closeButton.focus();
      }
    });

    // Event dla zewnętrznych integracji
    document.dispatchEvent(
      new CustomEvent('sygnisoftPopupOpened', {
        detail: { popupId: this.popupId },
      })
    );
  }

  close() {
    if (!this.isOpen || !this.module) return;

    this.module.classList.remove('is-visible');

    // Ukryj overlay tylko jeśli nie ma innych otwartych popupów
    const openPopups = document.querySelectorAll('.modals-block.is-visible');
    if (openPopups.length === 0 && this.overlayElement) {
      this.overlayElement.classList.remove('is-visible');
    }

    this.isOpen = false;

    // Przywróć scroll
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';

    // Przywróć focus
    if (this.previousActiveElement) {
      this.previousActiveElement.focus();
    }

    // Event dla zewnętrznych integracji
    document.dispatchEvent(
      new CustomEvent('sygnisoftPopupClosed', {
        detail: { popupId: this.popupId },
      })
    );
  }

  destroy() {
    // Cleanup event listeners
    if (this.escHandler) {
      document.removeEventListener('keydown', this.escHandler);
    }
    if (this.openHandler) {
      document.removeEventListener(
        `openPopup${this.popupId}`,
        this.openHandler
      );
    }
    if (this.overlayClickHandler && this.overlayElement) {
      this.overlayElement.removeEventListener(
        'click',
        this.overlayClickHandler
      );
    }
  }

  getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  }

  setCookie(name, value, days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    const expires = `expires=${date.toUTCString()}`;
    // Dodaj SameSite i Secure dla bezpieczeństwa
    const secure = location.protocol === 'https:' ? ';Secure' : '';
    document.cookie = `${name}=${value};${expires};path=/;SameSite=Lax${secure}`;
  }
}

// Globalna funkcja do otwierania popupów z walidacją
window.openSygnisoftPopup = function (popupId) {
  const id = parseInt(popupId, 10);
  if (isNaN(id) || id <= 0) {
    console.error('Invalid popup ID:', popupId);
    return;
  }
  document.dispatchEvent(new Event(`openPopup${id}`));
};

// Inicjalizacja wszystkich popupów
Project.ready(() => {
  document.querySelectorAll('.modals-block').forEach((module) => {
    const instance = new SygnisoftPopup(module);
    instance.init();
    module.sygnisoftPopupInstance = instance;
  });
});

export default SygnisoftPopup;
